import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authHeaders } from './Admin';
import { campaignName, keywordName } from '../campaigns';

import { API_BASE } from '../apiBase';
const TOKEN_KEY = 'gardeno_admin_token';

const C = {
  bg: '#0a0a0b',
  panel: '#141416',
  panelAlt: '#1a1a1d',
  border: '#262629',
  text: '#f2f2f3',
  sub: '#8a8a90',
  accent: '#2D9E5F',
};

// ---------------- Types (mirror the API contract) ----------------
interface CampaignRow {
  campaign: string;
  keyword: string;
  installs: number;
  trials: number;
  active: number;
  cancelled_trials: number;
  paid: number;
}
interface AttributionResp { campaigns: CampaignRow[] }

interface CampaignUser {
  email: string;
  name: string;
  plan: string;
  cancelled: boolean;
  created_at: string;
}
interface UsersResp { users: CampaignUser[] }

// One campaign's rolled-up totals plus the keyword rows behind it.
interface CampaignGroup {
  campaign: string;
  keywords: CampaignRow[];
  installs: number;
  trials: number;
  active: number;
  cancelled_trials: number;
  paid: number;
}

// What the drill-down modal is scoped to. keyword undefined = whole campaign.
interface Selection {
  campaign: string;
  keyword?: string;
  installs: number;
  trials: number;
  active: number;
  cancelled_trials: number;
  paid: number;
}

function isGuestEmail(email: string): boolean {
  return email.endsWith('@device.gardenoai.com');
}

function shortDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

const PLAN_COLOR: Record<string, string> = {
  Paid: '#34D399',
  Trial: '#3B82F6',
  Comp: '#A78BFA',
  Free: '#8a8a90',
};

// Install-date window presets. Dates are built in Asia/Karachi so "Today"
// matches the operator's calendar day; the backend parses them in the same
// zone. 'all' sends no bounds at all.
const PRESETS = [
  { key: 'all', label: 'All time' },
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7', label: 'Last 7 days' },
  { key: '30', label: 'Last 30 days' },
  { key: 'custom', label: 'Custom' },
] as const;
type PresetKey = typeof PRESETS[number]['key'];

function karachiDate(offsetDays: number): string {
  const now = new Date(Date.now() + offsetDays * 86_400_000);
  // en-CA gives YYYY-MM-DD directly.
  return now.toLocaleDateString('en-CA', { timeZone: 'Asia/Karachi' });
}

function rangeQuery(preset: PresetKey, customFrom: string, customTo: string): string {
  switch (preset) {
    case 'today':     return `from=${karachiDate(0)}&to=${karachiDate(0)}`;
    case 'yesterday': return `from=${karachiDate(-1)}&to=${karachiDate(-1)}`;
    case '7':         return `from=${karachiDate(-6)}&to=${karachiDate(0)}`;
    case '30':        return `from=${karachiDate(-29)}&to=${karachiDate(0)}`;
    case 'custom': {
      // Either bound may be left empty for an open-ended range.
      const parts = [];
      if (customFrom) parts.push(`from=${customFrom}`);
      if (customTo) parts.push(`to=${customTo}`);
      return parts.join('&');
    }
    default:          return '';
  }
}

export default function AcquisitionTab({ token, isMobile }: { token: string; isMobile: boolean }) {
  const navigate = useNavigate();
  const [preset, setPreset] = useState<PresetKey>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [campaigns, setCampaigns] = useState<CampaignRow[] | null>(null);
  const [loading, setLoading] = useState(false);

  // Drill-down modal state. `selected` describes what the modal is showing:
  // a whole campaign (keyword undefined) or a single keyword within one.
  const [selected, setSelected] = useState<Selection | null>(null);
  const [users, setUsers] = useState<CampaignUser[] | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = useCallback((campaign: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(campaign)) next.delete(campaign); else next.add(campaign);
      return next;
    });
  }, []);

  const on401 = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    window.location.reload();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rq = rangeQuery(preset, customFrom, customTo);
      const res = await fetch(`${API_BASE}/admin/attribution${rq ? `?${rq}` : ''}`, { headers: authHeaders(token) });
      if (res.status === 401 || res.status === 403) { on401(); return; }
      if (res.ok) {
        const d = await res.json();
        setCampaigns(((d.data ?? d) as AttributionResp).campaigns ?? []);
        // An open drill-down was for the previous window; close it.
        setSelected(null);
        setUsers(null);
      }
    } catch {
      /* silent — empty state */
    } finally {
      setLoading(false);
    }
  }, [token, on401, preset, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  // Roll keyword rows up into one row per campaign. Summing is safe: each
  // install carries a single attribution row, so no user is counted twice
  // across the keywords of a campaign.
  const groups = useMemo<CampaignGroup[]>(() => {
    const map = new Map<string, CampaignGroup>();
    for (const c of campaigns ?? []) {
      const g = map.get(c.campaign) ?? {
        campaign: c.campaign, keywords: [],
        installs: 0, trials: 0, active: 0, cancelled_trials: 0, paid: 0,
      };
      g.keywords.push(c);
      g.installs += c.installs;
      g.trials += c.trials;
      g.active += c.active;
      g.cancelled_trials += c.cancelled_trials;
      g.paid += c.paid;
      map.set(c.campaign, g);
    }
    const out = Array.from(map.values());
    out.forEach((g) => g.keywords.sort((a, b) => b.installs - a.installs));
    return out.sort((a, b) => b.installs - a.installs);
  }, [campaigns]);

  const openRow = useCallback(async (sel: Selection) => {
    setSelected(sel);
    setUsers(null);
    setUsersLoading(true);
    try {
      // Omitting the keyword param entirely asks the API for every user in the
      // campaign; sending it (even empty) scopes to that one keyword.
      const rq = rangeQuery(preset, customFrom, customTo);
      const q = `campaign=${encodeURIComponent(sel.campaign)}`
        + (sel.keyword === undefined ? '' : `&keyword=${encodeURIComponent(sel.keyword)}`)
        + (rq ? `&${rq}` : '');
      const res = await fetch(`${API_BASE}/admin/attribution/users?${q}`, { headers: authHeaders(token) });
      if (res.status === 401 || res.status === 403) { on401(); return; }
      if (res.ok) {
        const d = await res.json();
        setUsers(((d.data ?? d) as UsersResp).users ?? []);
      } else {
        setUsers([]);
      }
    } catch {
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [token, on401, preset, customFrom, customTo]);

  const HEADERS = ['Campaign', 'Keyword', 'Installs', 'Trials', 'Active', 'Cancelled trials', 'Paid', 'Install → Trial'];

  return (
    <div style={{ marginTop: 22 }}>
      <div style={{ fontSize: 18, fontWeight: 800 }}>Acquisition by campaign</div>
      <div style={{ fontSize: 13, color: C.sub, marginTop: 4, maxWidth: 680 }}>
        Where installs came from (Apple Search Ads), and how many started a trial, are active now, cancelled their trial, or became paying customers. "Organic" = installs not attributed to an ad. Click a campaign to break it down by keyword; click a keyword — or the Users button — to see the people behind it.
      </div>
      {/* Install-date window. Outcomes (trial / active / paid) are whatever
          happened to installs from that window, so "Last 7 days" reads as
          "of the people who installed this week, how many…". */}
      <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPreset(p.key)}
            style={{
              padding: '6px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${preset === p.key ? C.accent : C.border}`,
              background: preset === p.key ? C.accent : 'transparent',
              color: preset === p.key ? '#fff' : C.sub,
            }}
          >
            {p.label}
          </button>
        ))}
        {preset === 'custom' && (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input
              type="date"
              value={customFrom}
              max={customTo || undefined}
              onChange={(e) => setCustomFrom(e.target.value)}
              style={{ padding: '5px 8px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.panelAlt, color: C.text, fontSize: 12, colorScheme: 'dark' }}
            />
            <span style={{ color: C.sub, fontSize: 12 }}>to</span>
            <input
              type="date"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => setCustomTo(e.target.value)}
              style={{ padding: '5px 8px', borderRadius: 8, border: `1px solid ${C.border}`, background: C.panelAlt, color: C.text, fontSize: 12, colorScheme: 'dark' }}
            />
          </span>
        )}
      </div>
      <div style={{ marginTop: 14, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: C.sub }}>
                {HEADERS.map((h) => (
                  <th key={h} style={{ padding: '12px 10px', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const organic = g.campaign === 'organic';
                // Install → Trial, not → Paid: trials are the earliest signal a
                // campaign is working. Paid lags weeks behind the click.
                const conv = g.installs > 0 ? (g.trials / g.installs) * 100 : 0;
                const isOpen = expanded.has(g.campaign);
                // Organic (and any campaign whose only row has no keyword) has
                // nothing meaningful to expand into.
                const expandable = g.keywords.length > 1 || (g.keywords.length === 1 && !!g.keywords[0].keyword);
                const cell = (v: number, color: string, pad = '12px 10px') => (
                  <td style={{ padding: pad, color: v > 0 ? color : C.sub, fontWeight: v > 0 ? 700 : 400 }}>{v}</td>
                );
                return (
                  <React.Fragment key={g.campaign}>
                    <tr
                      onClick={() => (expandable ? toggle(g.campaign) : openRow({ ...g, keyword: g.keywords[0]?.keyword ?? '' }))}
                      style={{ borderTop: `1px solid ${C.border}`, cursor: 'pointer', background: isOpen ? C.panelAlt : 'transparent' }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = C.panelAlt)}
                      onMouseLeave={(e) => (e.currentTarget.style.background = isOpen ? C.panelAlt : 'transparent')}
                    >
                      <td style={{ padding: '12px 10px', whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'inline-block', width: 14, color: C.sub, fontSize: 11 }}>
                          {expandable ? (isOpen ? '▾' : '▸') : ''}
                        </span>
                        {organic
                          ? <span style={{ color: C.sub }}>Organic</span>
                          : <span style={{ fontWeight: 700 }}>{campaignName(g.campaign)}</span>}
                      </td>
                      <td style={{ padding: '12px 10px', whiteSpace: 'nowrap', color: C.sub }}>
                        {expandable ? `${g.keywords.length} keyword${g.keywords.length === 1 ? '' : 's'}` : '—'}
                      </td>
                      <td style={{ padding: '12px 10px', fontWeight: 700 }}>{g.installs}</td>
                      {cell(g.trials, C.text)}
                      {cell(g.active, '#3B82F6')}
                      {cell(g.cancelled_trials, '#F87171')}
                      {cell(g.paid, C.accent)}
                      <td style={{ padding: '12px 10px', color: C.sub, whiteSpace: 'nowrap' }}>
                        {conv.toFixed(1)}%
                        <button
                          onClick={(e) => { e.stopPropagation(); openRow({ ...g, keyword: undefined }); }}
                          style={{
                            marginLeft: 12, background: 'none', border: `1px solid ${C.border}`,
                            borderRadius: 6, padding: '3px 9px', color: C.sub, cursor: 'pointer', fontSize: 12,
                          }}
                        >Users</button>
                      </td>
                    </tr>
                    {isOpen && g.keywords.map((c, ki) => {
                      const kconv = c.installs > 0 ? (c.trials / c.installs) * 100 : 0;
                      return (
                        <tr
                          key={`${g.campaign}-${c.keyword}-${ki}`}
                          onClick={() => openRow({ ...c, keyword: c.keyword })}
                          style={{ borderTop: `1px solid ${C.border}`, cursor: 'pointer', fontSize: 13.5 }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = C.panelAlt)}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <td style={{ padding: '9px 10px' }} />
                          <td style={{ padding: '9px 10px', whiteSpace: 'nowrap', color: c.keyword ? C.text : C.sub }}>
                            <span style={{ color: C.sub, marginRight: 8 }}>└</span>
                            {c.keyword ? keywordName(c.keyword) : '(no keyword)'}
                          </td>
                          <td style={{ padding: '9px 10px', fontWeight: 600 }}>{c.installs}</td>
                          {cell(c.trials, C.text, '9px 10px')}
                          {cell(c.active, '#3B82F6', '9px 10px')}
                          {cell(c.cancelled_trials, '#F87171', '9px 10px')}
                          {cell(c.paid, C.accent, '9px 10px')}
                          <td style={{ padding: '9px 10px', color: C.sub }}>{kconv.toFixed(1)}%</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {!loading && groups.length === 0 && (
                <tr><td colSpan={HEADERS.length} style={{ padding: '28px 18px', textAlign: 'center', color: C.sub }}>No attribution data yet. New installs from the next app build will start appearing here.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={HEADERS.length} style={{ padding: '28px 18px', textAlign: 'center', color: C.sub }}>Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drill-down: users behind the clicked row. */}
      {selected && (
        <div
          onClick={() => setSelected(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 50,
            display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.panel, border: `1px solid ${C.border}`, borderRadius: isMobile ? '16px 16px 0 0' : 14,
              width: isMobile ? '100%' : 'min(720px, 100%)', maxHeight: isMobile ? '85vh' : '80vh', overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800 }}>
                  {selected.campaign === 'organic' ? 'Organic' : campaignName(selected.campaign)}
                  <span style={{ color: C.sub, fontWeight: 500 }}>
                    {selected.keyword === undefined
                      ? ' · all keywords'
                      : selected.keyword ? ` · ${keywordName(selected.keyword)}` : ''}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: C.sub, marginTop: 3 }}>
                  {selected.installs} installs · {selected.trials} trials · {selected.active} active · {selected.cancelled_trials} cancelled · {selected.paid} paid
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: C.panelAlt, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 12px', color: C.text, cursor: 'pointer', fontSize: 13 }}>Close</button>
            </div>
            <div style={{ overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: C.sub }}>
                    {['User', 'Plan', 'Joined'].map((h) => (
                      <th key={h} style={{ padding: '10px 16px', fontWeight: 600, position: 'sticky', top: 0, background: C.panel }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(users ?? []).map((u, i) => {
                    const guest = isGuestEmail(u.email);
                    const label = u.name || (guest ? 'Guest device' : u.email);
                    return (
                      <tr
                        key={i}
                        onClick={() => navigate(`/admin/user/${encodeURIComponent(u.email)}`)}
                        style={{ borderTop: `1px solid ${C.border}`, cursor: 'pointer' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = C.panelAlt)}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <td style={{ padding: '11px 16px' }}>
                          <div style={{ fontWeight: 600 }}>{label}</div>
                          {!guest && u.name && <div style={{ fontSize: 12, color: C.sub }}>{u.email}</div>}
                        </td>
                        <td style={{ padding: '11px 16px', whiteSpace: 'nowrap' }}>
                          <span style={{ color: PLAN_COLOR[u.plan] || C.sub, fontWeight: 700 }}>{u.plan}</span>
                          {u.cancelled && <span style={{ color: '#F87171', fontSize: 12, marginLeft: 8 }}>cancelled</span>}
                        </td>
                        <td style={{ padding: '11px 16px', color: C.sub, whiteSpace: 'nowrap' }}>{shortDate(u.created_at)}</td>
                      </tr>
                    );
                  })}
                  {!usersLoading && (users ?? []).length === 0 && (
                    <tr><td colSpan={3} style={{ padding: '28px 18px', textAlign: 'center', color: C.sub }}>No users to show for this row.</td></tr>
                  )}
                  {usersLoading && (
                    <tr><td colSpan={3} style={{ padding: '28px 18px', textAlign: 'center', color: C.sub }}>Loading…</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
